//React
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

//External Packages
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripLinesVertical } from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";

//Internal Components
import { isDayComplete } from "../../../utils/dayProgress";

import {
  CreateDayIconV2,
  ValuesIcon,
  VitIcon,
  VisionIcon,
  IkigaiIcon,
  QuoteIconV2,
  HideOverlayIcon,
  SaveIcon,
  PreviousDay,
  OneIcon,
  TwoIcon,
  ThreeIcon,
  FourIcon,
  FiveIcon,
  SixIcon,
  SevenIcon,
  EightIcon,
  NineIcon,
  TenIcon,
} from "../../../components/ui/Icons/Icon";

// Internal Components
import AiVoiceCapture from "../../../features/voice/AiVoiceCapture";
import { labelMaker } from "../../../utils/UtilityFunctions";

// Sanity Client
import { client } from "../../../services/client";
import { faCirclePause, faCirclePlay } from "@fortawesome/free-solid-svg-icons";

//CSS Style
import "./dock.css";

export default function FullDock({
  today,
  previousDay,
  objectives,
  handleMouseState,
  handleColumnChange,
  handleSubmit,
  handleTodayAiChange,
  load,
  yearList,
  meyear,
  handlePlayingChange,
}) {
  // Hooks
  const navigate = useNavigate();

  // State
  const [isVisible, setIsVisible] = useState(true);
  const [colorState, setColorState] = useState({
    one: "cls-1-invert",
    two: "cls-2-invert",
  });
  const [viewWidth, setViewWidth] = useState(
    Math.max(document.documentElement.clientWidth),
  );
  const [saveTooltip, setSaveTooltip] = useState("");

  const [audio, setAudio] = useState();
  const [hasShownNudge, setHasShownNudge] = useState(false);
  const [nudgeVisible, setNudgeVisible] = useState(false);

  useEffect(() => {
    if (
      load === "success" &&
      !hasShownNudge &&
      !localStorage.getItem("nwd_donated")
    ) {
      setNudgeVisible(true);
      setHasShownNudge(true);
      const timer = setTimeout(() => setNudgeVisible(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [load]);

  const [test, setTest] = useState(false);

  async function getAudio() {
    const audio = await client.fetch(`
            *[_type == "audio"]{
              _id,
              title,
              "audioUrl": audioFile.asset->url
            }
          `);
    return audio;
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getAudio();
        if (response) {
          setAudio(response);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  function AudioPlayer({ audioUrl }) {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const togglePlay = () => {
      const audioElement = audioRef.current;
      if (audioElement) {
        if (isPlaying) {
          audioElement.pause();
        } else {
          audioElement.play();
        }
        setIsPlaying(!isPlaying);
        handlePlayingChange(isPlaying);
      }
    };

    return (
      <div onClick={togglePlay} style={{ height: "100%", width: "100%" }}>
        <audio ref={audioRef} src={audioUrl} controls={false} />
        <div style={{ height: "100%", width: "100%" }}>
          {isPlaying ? (
            <FontAwesomeIcon
              icon={faCirclePause}
              style={{ height: "100%", width: "100%", color: "#004a22" }}
            />
          ) : (
            <FontAwesomeIcon
              icon={faCirclePlay}
              style={{ height: "100%", width: "100%", color: "#004a22" }}
            />
          )}
        </div>
      </div>
    );
  }

  const handleTooltipChange = () => {
    setSaveTooltip("Saving...");
    setTimeout(() => {
      setSaveTooltip("Saved");
    }, 1200);
  };
  const [createDayState, setCreateDayState] = useState({
    active: true,
    text: "",
  });

  const [previousDayState, setPreviousDayState] = useState({
    active: true,
    text: "",
  });

  const [liveObjectives, setLiveObjectives] = useState([]);

  let numIconObj = {
    "obj-one": OneIcon,
    "obj-two": TwoIcon,
    "obj-three": ThreeIcon,
    "obj-four": FourIcon,
    "obj-five": FiveIcon,
    "obj-six": SixIcon,
    "obj-seven": SevenIcon,
    "obj-eight": EightIcon,
    "obj-nine": NineIcon,
    "obj-ten": TenIcon,
  };

  const filteredArray = Object.entries(objectives)
    .filter(([key, obj]) => obj.title.trim() !== "")
    .map(([key, obj]) => ({ key, icon: numIconObj[key], ...obj }));

  const createDayHandler = () => {
    const complete = isDayComplete(today);

    setCreateDayState({
      active: complete,
      text: complete
        ? "Ready to start a new day?"
        : "Complete your day to start a new one.",
    });
  };

  useEffect(() => {
    createDayHandler();
  }, [today]);

  // Gate on whether there actually is a previous day. This used to test
  // `yearList`, which has nothing to do with days, so the control was always
  // active and always linked somewhere.
  const previousDayHandler = () => {
    if (!previousDay) {
      setPreviousDayState((prevState) => {
        let shallow = { ...prevState };
        shallow.active = false;
        shallow.text = "You are yet to log multiple days.";
        return shallow;
      });
    } else {
      setPreviousDayState((prevState) => {
        let shallow = { ...prevState };
        shallow.active = true;
        shallow.text = "Look back at the previous day.";
        return shallow;
      });
    }
  };

  useEffect(() => {
    previousDayHandler();
  }, [previousDay]);

  let leng = yearList && yearList.years && yearList.years.length - 1;

  return (
    <section className="dock--section">
      <motion.div
        initial={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className={"dock--container"}
      >
        <div className={`jordandock node`} data-tooltip={createDayState.text}>
          {/* Disable if endScore is empty or if moment is missing a momentScore */}
          {createDayState.active ? (
            <a href="/today">
              <CreateDayIconV2 />
            </a>
          ) : (
            <CreateDayIconV2 opacity="0.5" />
          )}
        </div>
        <div
          // Ensure this node is disabled if no previous day exists
          className={`jordandock node`}
          data-tooltip={previousDayState.text}
        >
          {previousDayState.active ? (
            <a href={previousDay}>
              <PreviousDay />
            </a>
          ) : (
            <PreviousDay opacity="0.5" />
          )}
        </div>

        {/* <AiVoiceCapture handleTodayAiChange={handleTodayAiChange} /> */}

        <div className="dock separator">
          <FontAwesomeIcon
            id="separatorIcon"
            className="separator"
            icon={faGripLinesVertical}
          />
        </div>

        <div
          className={`jordandock node`}
          // Replace with passing year, initialising it as state and then calling the name key

          data-tooltip={
            meyear && meyear.obj && meyear.obj.name && meyear.obj.birthday
              ? "Me"
              : "Add your name and date of birth to your year map to access it in the dock."
          }
          onClick={() => handleMouseState("me")}
        >
          {meyear && meyear.obj && meyear.obj.name && meyear.obj.birthday ? (
            <VitIcon />
          ) : (
            <a
              href={
                yearList && yearList.years && yearList.years.length > 0
                  ? `/year/${yearList.years[leng]._id}`
                  : "/year/create"
              }
            >
              <VitIcon opacity="0.5" />
            </a>
          )}
        </div>

        <div
          style={{ height: "40px", width: "40px" }}
          className={`jordandock node`}
          data-tooltip={
            meyear && meyear.obj && meyear.obj.values
              ? "My Values"
              : "Add your values in your year map to access them in the dock."
          }
          onClick={() => handleMouseState("values")}
        >
          {meyear && meyear.obj && meyear.obj.values ? (
            <ValuesIcon />
          ) : (
            <a
              href={
                yearList && yearList.years && yearList.years.length > 0
                  ? `/year/${yearList.years[leng]._id}`
                  : "/year/create"
              }
            >
              <ValuesIcon opacity="0.5" />
            </a>
          )}
        </div>

        <div
          className={`jordandock node`}
          data-tooltip={
            meyear && meyear.obj && meyear.obj.vision
              ? "My Vision"
              : "Add your vision to your year map to access it through the dock."
          }
          onClick={() => handleMouseState("vision")}
        >
          {meyear && meyear.obj && meyear.obj.vision ? (
            <VisionIcon />
          ) : (
            <a
              href={
                yearList && yearList.years && yearList.years.length > 0
                  ? `/year/${yearList.years[leng]._id}`
                  : "/year/create"
              }
            >
              <VisionIcon opacity="0.5" />
            </a>
          )}
        </div>

        <div
          className={`jordandock node`}
          data-tooltip={
            meyear && meyear.obj && meyear.obj.ikigai
              ? "My Ikigai"
              : "Add your ikigai to your year map to access it in the dock"
          }
          onClick={() => handleMouseState("ikigai")}
        >
          {meyear && meyear.obj && meyear.obj.ikigai ? (
            <IkigaiIcon />
          ) : (
            <a
              href={
                yearList && yearList.years && yearList.years.length > 0
                  ? `/year/${yearList.years[leng]._id}`
                  : "/year/create"
              }
            >
              <IkigaiIcon opacity="0.5" />
            </a>
          )}
        </div>
        <div
          className={`jordandock node`}
          data-tooltip={
            meyear && meyear.obj && meyear.obj.quote
              ? "My Favourite Quote"
              : "Add your favourite quote to your year map to access it in the dock."
          }
          onClick={() => handleMouseState("quote")}
        >
          {meyear && meyear.obj && meyear.obj.quote ? (
            <QuoteIconV2 />
          ) : (
            <a
              href={
                yearList && yearList.years && yearList.years.length > 0
                  ? `/year/${yearList.years[leng]._id}`
                  : "/year/create"
              }
            >
              <QuoteIconV2 opacity="0.5" />
            </a>
          )}
        </div>

        {/* <div className={`jordandockwide node`} data-tooltip="Hide Overlay">
          <HideOverlayIcon />
        </div> */}

        <div className="dock separator">
          <FontAwesomeIcon
            id="separatorIcon"
            className="separator"
            icon={faGripLinesVertical}
          />
        </div>

        {filteredArray.map((objective, index) => {
          const IconComponent = objective.icon;

          return (
            <div
              key={index}
              className="jordandock node"
              data-tooltip={
                objectives[objective.key]?.title
                  ? objectives[objective.key].title.slice(0, 20).length >= 20
                    ? objectives[objective.key].title.slice(0, 20) + "..."
                    : objectives[objective.key].title
                  : "Add an objective into your week planner to access it here."
              }
              data-name={objective.key}
              onClick={(e) => handleColumnChange(e)}
            >
              {objectives && IconComponent && (
                <IconComponent
                  opacity={objectives[objective.key]?.score}
                  text={
                    objectives[objective.key]?.title &&
                    labelMaker(objectives[objective.key].title)
                  }
                />
              )}
            </div>
          );
        })}

        {filteredArray.length > 0 && (
          <div className="dock separator">
            <FontAwesomeIcon
              id="separatorIcon"
              className="separator"
              icon={faGripLinesVertical}
            />
          </div>
        )}

        <div
          className={`jordandock node_save ${load}`}
          data-tooltip={load === "success" ? saveTooltip : "Click to Save"}
          onClick={(e) => {
            handleSubmit(e);
            handleTooltipChange();
          }}
          style={{ position: "relative" }}
        >
          <SaveIcon load={load} />
        </div>
      </motion.div>
    </section>
  );
}
